import React, { useState, useEffect, Fragment, useMemo } from 'react';
import { Card, CardBody, CardHeader, Button, Col, Container, Row, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input } from 'reactstrap';
import { MRT_ToggleDensePaddingButton, MRT_ToggleFiltersButton, MRT_ToggleFullScreenButton, MaterialReactTable } from 'material-react-table';
import { Box } from '@mui/material';
import { getGlobalFeatures, createGlobalFeature, getFeatureEnabledBps, getAllPartnershipListBYRole, upsertBpFeature, getBpFeatureLogs } from '../../../../api';
import { toast } from 'react-toastify';
import Breadcrumb from '../../../../layout/breadcrumb';
import Switch from '@mui/material/Switch';

// ─── Color palette ────────────────────────────────────────────────────────────
const PRIMARY   = '#1565c0';
const SUCCESS   = '#2e7d32';
const DANGER    = '#c62828';

const FeatureManagement = () => {
    const [features, setFeatures] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Add/Edit Feature Modal
    const [modalOpen, setModalOpen] = useState(false);
    const [featureKey, setFeatureKey] = useState('');
    const [featureName, setFeatureName] = useState('');
    const [description, setDescription] = useState('');
    const [isActive, setIsActive] = useState(true);

    // Assign BPs Modal
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [isAssignLoading, setIsAssignLoading] = useState(false);
    const [allBps, setAllBps] = useState([]);
    const [enabledBpIds, setEnabledBpIds] = useState(new Set());
    const [selectedFeature, setSelectedFeature] = useState(null);

    // ── Feature-Enabled filter for BP table ('all' | 'enabled' | 'disabled')
    const [featureEnabledFilter, setFeatureEnabledFilter] = useState('all');

    // Pagination States for BPs Table
    const [columnFilters, setColumnFilters] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState([]);
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
    const [totalRowCount, setTotalRowCount] = useState(0);

    // Audit Trail Modal
    const [auditModalOpen, setAuditModalOpen] = useState(false);
    const [auditLoading, setAuditLoading] = useState(false);
    const [auditLogs, setAuditLogs] = useState([]);
    const [selectedAuditBp, setSelectedAuditBp] = useState(null);

    // Export loading
    const [exportLoading, setExportLoading] = useState(false);

    const fetchFeatures = async () => {
        setIsLoading(true);
        try {
            const res = await getGlobalFeatures();
            setFeatures(res.data?.result?.result || []);
        } catch (error) {
            toast.error(error?.response?.error || 'Failed to fetch features');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { fetchFeatures(); }, []);

    const toggleModal = () => setModalOpen(!modalOpen);

    const toggleAssignModal = () => {
        setAssignModalOpen(!assignModalOpen);
        if (assignModalOpen) {
            setPagination({ pageIndex: 0, pageSize: 10 });
            setGlobalFilter('');
            setColumnFilters([]);
            setSorting([]);
            setFeatureEnabledFilter('all');
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            await createGlobalFeature({ featureKey, featureName, description, isActive });
            toast.success('Feature saved successfully');
            toggleModal();
            fetchFeatures();
        } catch (error) {
            toast.error(error?.response?.error || 'Failed to save feature');
        }
    };

    const handleToggleActive = async (row) => {
        try {
            await createGlobalFeature({
                featureKey: row.original.feature_key,
                featureName: row.original.feature_name,
                description: row.original.description,
                isActive: !row.original.is_active,
            });
            toast.success('Feature status updated');
            fetchFeatures();
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const fetchBpsForModal = async () => {
        if (!selectedFeature) return;
        setIsAssignLoading(true);
        try {
            const bpsRes = await getAllPartnershipListBYRole(
                pagination.pageIndex, pagination.pageSize,
                globalFilter || '', columnFilters, sorting, '43'
            );
            setAllBps(bpsRes.data?.result?.partnershipData || []);
            setTotalRowCount(bpsRes.data?.result?.totalCount || 0);

            const enabledRes = await getFeatureEnabledBps(selectedFeature.feature_key);
            const enabledSet = new Set(enabledRes.data?.result?.result || []);
            setEnabledBpIds(enabledSet);
        } catch (error) {
            toast.error(error?.response?.error || 'Failed to load BPs');
        } finally {
            setIsAssignLoading(false);
        }
    };

    useEffect(() => {
        if (assignModalOpen && selectedFeature) {
            const t = setTimeout(fetchBpsForModal, 500);
            return () => clearTimeout(t);
        }
    }, [assignModalOpen, selectedFeature, pagination.pageIndex, pagination.pageSize, globalFilter, columnFilters, sorting]);

    const openAssignModal = (feature) => {
        setSelectedFeature(feature);
        setAssignModalOpen(true);
    };

    const toggleAuditModal = () => {
        setAuditModalOpen(!auditModalOpen);
        if (auditModalOpen) { setAuditLogs([]); setSelectedAuditBp(null); }
    };

    const handleOpenAuditModal = async (bp) => {
        setSelectedAuditBp(bp);
        setAuditModalOpen(true);
        setAuditLoading(true);
        try {
            const res = await getBpFeatureLogs(bp.partnership_id, selectedFeature.feature_key);
            setAuditLogs(res.data?.result || []);
        } catch (error) {
            toast.error(error?.response?.error || 'Failed to fetch audit logs');
        } finally {
            setAuditLoading(false);
        }
    };

    const handleToggleBpFeature = async (bpId, currentStatus) => {
        const newStatus = !currentStatus;
        try {
            const ROLE = JSON.parse(localStorage.getItem('ROLE'));
            const activeRole = ROLE?.alias || ROLE?.name || '';
            await upsertBpFeature(bpId, {
                featureKey: selectedFeature.feature_key,
                isActive: newStatus,
                activeRole,
            });
            toast.success('BP Feature updated successfully');
            setEnabledBpIds(prev => {
                const next = new Set(prev);
                if (newStatus) next.add(bpId); else next.delete(bpId);
                return next;
            });
        } catch (error) {
            toast.error('Failed to update BP feature');
        }
    };

    // ── Export only the currently visible BPs (filteredBps) ──────────────────
    const handleExport = () => {
        if (!selectedFeature || filteredBps.length === 0) {
            toast.warn('No data to export');
            return;
        }
        setExportLoading(true);
        try {
            // Use already-loaded state — no extra API call needed
            const headers = ['BP Code', 'Business Partner Name', 'Email', `${selectedFeature.feature_name} Enabled`];
            const rows = filteredBps.map(bp => [
                bp.partnership_code || bp.partnership_id,
                `"${(bp.name || '').replace(/"/g, '""')}"`,
                bp.email || '',
                enabledBpIds.has(bp.partnership_id) ? 'Yes' : 'No',
            ]);

            const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${selectedFeature.feature_key}_bp_export.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            toast.success(`Exported ${filteredBps.length} records`);
        } catch (error) {
            toast.error('Failed to export data');
        } finally {
            setExportLoading(false);
        }
    };

    // ── Filtered BPs based on Feature Enabled filter ──────────────────────────
    const filteredBps = useMemo(() => {
        if (featureEnabledFilter === 'enabled') return allBps.filter(bp => enabledBpIds.has(bp.partnership_id));
        if (featureEnabledFilter === 'disabled') return allBps.filter(bp => !enabledBpIds.has(bp.partnership_id));
        return allBps;
    }, [allBps, enabledBpIds, featureEnabledFilter]);

    // ── Main features table columns (Feature Key removed) ────────────────────
    const columns = useMemo(() => [
        {
            header: 'Feature Name',
            accessorKey: 'feature_name',
        },
        {
            header: 'Description',
            accessorKey: 'description',
        },
        {
            header: 'Active',
            accessorKey: 'is_active',
            Cell: ({ row }) => (
                <div className="d-flex align-items-center gap-2">
                    <span className={row.original.is_active ? 'badge badge-success' : 'badge badge-danger'}>
                        {row.original.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <Switch
                        checked={row.original.is_active}
                        onChange={() => handleToggleActive(row)}
                        color="primary"
                    />
                </div>
            ),
        },
        {
            header: 'Actions',
            accessorKey: 'actions',
            enableSorting: false,
            Cell: ({ row }) => (
                <Button color="info" size="sm" onClick={() => openAssignModal(row.original)}>
                    <i className="fa fa-users me-2"></i> Assign BPs
                </Button>
            ),
        },
    ], []);

    // ── BP table columns: separate Audit Trail column, BP Code instead of ID ─
    const bpColumns = useMemo(() => [
        {
            header: 'BP Code',
            accessorKey: 'partnership_code',
            size: 110,
            Cell: ({ row }) => (
                <span style={{ fontWeight: 600, color: PRIMARY }}>
                    {row.original.partnership_code || '—'}
                </span>
            ),
        },
        {
            header: 'Business Partner Name',
            accessorKey: 'name',
        },
        {
            header: 'Email ID',
            accessorKey: 'email',
        },
        {
            header: 'Feature Enabled',
            accessorKey: 'enabled',
            size: 140,
            // Custom filter element: All / Enabled / Disabled
            Filter: () => (
                <select
                    value={featureEnabledFilter}
                    onChange={e => setFeatureEnabledFilter(e.target.value)}
                    style={{
                        width: '100%',
                        padding: '4px 6px',
                        border: '1px solid #cbd5e1',
                        borderRadius: 4,
                        fontSize: '0.8rem',
                        color: '#334155',
                        background: '#fff',
                    }}
                >
                    <option value="all">All</option>
                    <option value="enabled">Enabled</option>
                    <option value="disabled">Disabled</option>
                </select>
            ),
            enableColumnFilter: true,
            Cell: ({ row }) => {
                const bpId = row.original.partnership_id;
                const isEnabled = enabledBpIds.has(bpId);
                return (
                    <div className="d-flex align-items-center gap-1">
                        <Switch
                            checked={isEnabled}
                            onChange={() => handleToggleBpFeature(bpId, isEnabled)}
                            size="small"
                        />
                        <span
                            style={{
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                color: isEnabled ? SUCCESS : DANGER,
                                background: isEnabled ? '#e8f5e9' : '#fdecea',
                                borderRadius: 10,
                                padding: '2px 8px',
                            }}
                        >
                            {isEnabled ? 'On' : 'Off'}
                        </span>
                    </div>
                );
            },
        },
        {
            header: 'Audit Trail',
            id: 'audit_trail',
            size: 110,
            enableSorting: false,
            enableColumnFilter: false,
            Cell: ({ row }) => (
                <Button
                    color="secondary"
                    size="sm"
                    outline
                    onClick={() => handleOpenAuditModal(row.original)}
                    title="View Audit Trail"
                    style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                >
                    <i className="fa fa-history"></i> History
                </Button>
            ),
        },
    ], [enabledBpIds, selectedFeature, featureEnabledFilter]);

    return (
        <Fragment>
            <Breadcrumb parent="Affiliations" title="Feature Management" />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center pb-0 border-0">
                                <h5><i className="fa fa-cogs me-2"></i> Master Feature Management</h5>
                                <Button color="primary" onClick={() => {
                                    setFeatureKey('');
                                    setFeatureName('');
                                    setDescription('');
                                    setIsActive(true);
                                    toggleModal();
                                }}>
                                    <i className="fa fa-plus me-2"></i> Add Feature
                                </Button>
                            </CardHeader>
                            <CardBody>
                                <MaterialReactTable
                                    columns={columns}
                                    data={features}
                                    state={{ isLoading }}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* ══ Add Feature Modal ══ */}
            <Modal isOpen={modalOpen} toggle={toggleModal}>
                <ModalHeader toggle={toggleModal}>Add/Edit Feature</ModalHeader>
                <Form onSubmit={handleSave}>
                    <ModalBody>
                        <FormGroup>
                            <Label>Feature Key</Label>
                            <Input type="text" value={featureKey} onChange={(e) => setFeatureKey(e.target.value)} required placeholder="e.g. coi_branding" />
                        </FormGroup>
                        <FormGroup>
                            <Label>Feature Name</Label>
                            <Input type="text" value={featureName} onChange={(e) => setFeatureName(e.target.value)} required placeholder="e.g. COI Branding" />
                        </FormGroup>
                        <FormGroup>
                            <Label>Description</Label>
                            <Input type="textarea" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description of the feature" />
                        </FormGroup>
                        <FormGroup check>
                            <Label check>
                                <Input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                                {' '}Is Active
                            </Label>
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" type="submit">Save</Button>
                        <Button color="secondary" onClick={toggleModal}>Cancel</Button>
                    </ModalFooter>
                </Form>
            </Modal>

            {/* ══ Assign BPs Modal ══ */}
            <Modal isOpen={assignModalOpen} toggle={toggleAssignModal} size="xl">
                <ModalHeader toggle={toggleAssignModal}>
                    <i className="fa fa-sitemap me-2"></i> Assign Feature: {selectedFeature?.feature_name}
                </ModalHeader>
                <ModalBody style={{ padding: 0 }}>
                    <MaterialReactTable
                        columns={bpColumns}
                        data={filteredBps}
                        enableGlobalFilter={false}
                        columnResizeMode="onChange"
                        initialState={{ showColumnFilters: false, density: 'compact' }}
                        defaultColumn={{ maxSize: 400, minSize: 80, size: 150 }}
                        manualPagination
                        manualFiltering
                        manualSorting
                        onColumnFiltersChange={(e) => {
                            setColumnFilters(e);
                            setPagination(prev => ({ ...prev, pageIndex: 0 }));
                        }}
                        onGlobalFilterChange={(e) => {
                            setGlobalFilter(e);
                            setPagination(prev => ({ ...prev, pageIndex: 0 }));
                        }}
                        onPaginationChange={setPagination}
                        onSortingChange={setSorting}
                        rowCount={featureEnabledFilter === 'all' ? totalRowCount : filteredBps.length}
                        state={{
                            isLoading: isAssignLoading,
                            columnFilters,
                            globalFilter,
                            pagination,
                            sorting,
                        }}
                        renderTopToolbarCustomActions={() => (
                            <Box sx={{ display: 'flex', gap: '0.5rem', p: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                {/* Feature Enabled quick-filter pills */}
                                {/* <span style={{ fontSize: '0.82rem', color: '#475569', fontWeight: 600, marginRight: 4 }}>Filter:</span>
                                {[
                                    { label: 'All', value: 'all' },
                                    { label: '✓ Enabled', value: 'enabled' },
                                    { label: '✗ Disabled', value: 'disabled' },
                                ].map(opt => (
                                    <button
                                        key={opt.value}
                                        onClick={() => setFeatureEnabledFilter(opt.value)}
                                        style={{
                                            padding: '3px 12px',
                                            borderRadius: 20,
                                            border: '1px solid',
                                            borderColor: featureEnabledFilter === opt.value
                                                ? (opt.value === 'enabled' ? SUCCESS : opt.value === 'disabled' ? DANGER : PRIMARY)
                                                : '#cbd5e1',
                                            background: featureEnabledFilter === opt.value
                                                ? (opt.value === 'enabled' ? '#e8f5e9' : opt.value === 'disabled' ? '#fdecea' : '#e3f0ff')
                                                : '#fff',
                                            color: featureEnabledFilter === opt.value
                                                ? (opt.value === 'enabled' ? SUCCESS : opt.value === 'disabled' ? DANGER : PRIMARY)
                                                : '#64748b',
                                            fontWeight: featureEnabledFilter === opt.value ? 700 : 500,
                                            fontSize: '0.78rem',
                                            cursor: 'pointer',
                                            transition: 'all 0.15s',
                                        }}
                                    >
                                        {opt.label}
                                    </button>
                                ))} */}
                                {/* Export CSV button */}
                                <Button
                                    color="success"
                                    size="sm"
                                    onClick={handleExport}
                                    disabled={exportLoading}
                                    style={{ display: 'flex', alignItems: 'center', gap: 5, marginLeft: 8 }}
                                >
                                    <i className={`fa ${exportLoading ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
                                    {exportLoading ? 'Exporting…' : 'Export CSV'}
                                </Button>
                            </Box>
                        )}
                        renderToolbarInternalActions={({ table }) => (
                            <Box>
                                <MRT_ToggleFiltersButton table={table} />
                                <MRT_ToggleDensePaddingButton table={table} />
                                <MRT_ToggleFullScreenButton table={table} />
                            </Box>
                        )}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleAssignModal}>Close</Button>
                </ModalFooter>
            </Modal>

            {/* ══ Audit Trail Modal ══ */}
            <Modal isOpen={auditModalOpen} toggle={toggleAuditModal} size="xl">
                <ModalHeader toggle={toggleAuditModal}>
                    <i className="fa fa-history me-2"></i> Audit Trail: {selectedAuditBp?.name} - {selectedFeature?.feature_name}
                </ModalHeader>
                <ModalBody>
                    <MaterialReactTable
                        columns={[
                            { header: 'Action', accessorKey: 'action', size: 100 },
                            { header: 'Feature Name', accessorFn: () => selectedFeature?.feature_name || 'N/A' },
                            {
                                header: 'Previous Status',
                                accessorKey: 'previous_status',
                                Cell: ({ row }) => row.original.previous_status ? 'Enabled' : 'Disabled',
                            },
                            {
                                header: 'Date & Time',
                                accessorKey: 'createdAt',
                                Cell: ({ row }) => new Date(row.original.createdAt).toLocaleString(),
                            },
                            { header: 'User Name', accessorKey: 'changed_by_user_name' },
                            { header: 'User ID', accessorKey: 'changed_by_user_id', size: 100 },
                            { header: 'Role', accessorKey: 'changed_by_user_role', size: 100 },
                        ]}
                        data={auditLogs}
                        enableGlobalFilter={false}
                        enablePagination={true}
                        enableSorting={true}
                        state={{ isLoading: auditLoading }}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleAuditModal}>Close</Button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
};

export default FeatureManagement;
